import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TooltipOrientationEnum, TooltipProps } from './tooltip.type'

import { generatePortalClasses } from './tooltip.constants'

import styles from './tooltip.module.css'

export const Tooltip = ({
  children,
  className,
  textClassname,
  orientation = TooltipOrientationEnum.bottom,
  text,
  widthText,
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const generatedClasses = generatePortalClasses(orientation)

  useEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()

    let top = 0
    let left = 0

    switch (orientation) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - 8
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        break
      case 'bottom':
        top = triggerRect.bottom + 8
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        break
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        left = triggerRect.left - tooltipRect.width - 8
        break
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        left = triggerRect.right + 8
        break
    }

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    if (left < 0) left = 8
    if (left + tooltipRect.width > viewportWidth)
      left = viewportWidth - tooltipRect.width - 8
    if (top < 0) top = 8
    if (top + tooltipRect.height > viewportHeight)
      top = viewportHeight - tooltipRect.height - 8

    setPosition({ top, left })
  }, [isVisible, orientation])

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      clearHideTimeout()
    }
  }, [])

  const handleMouseEnter = () => {
    clearHideTimeout()
    setIsVisible(true)
  }

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 100)
  }

  const handleTooltipMouseEnter = () => {
    clearHideTimeout()
    setIsVisible(true)
  }

  const handleTooltipMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 100)
  }

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={`${generatedClasses} ${textClassname || ''}`}
      style={{
        width: widthText || undefined,
        position: 'fixed',
        top: position.top,
        left: position.left,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        zIndex: 9999,
      }}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      {text}
    </div>
  )

  return (
    <div
      ref={triggerRef}
      className={`${styles.core} ${className || ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isVisible && createPortal(tooltipContent, document.body)}
      {children}
    </div>
  )
}
